import { useState, useCallback, ChangeEvent, useEffect, useRef } from 'react';
import './Search.css';
import { SquiggleItem } from './types/types';
import { useAdventureDataContext } from './context/AdventureDataContext';
import { useOpenItemsContext } from './context/OpenItemsContext';
import { cleanTextForSearch } from './biz/markdownLite';

interface SearchResults {
  item: {
    id?: string;
    type?: string;
    name: string;
  };
  nameHitPos?: number;
  textHitCount: number;
  excerpts: string[];
}

function Search({
  openItem,
  close
} : {
  openItem: (itemId: string) => void,
  close: (lastOpenedItemId: string|null) => void
}) {
  const [searchString, setSearchString] = useState<string>("");
  const { allUserItems } = useAdventureDataContext();
  const { selectedItemId, setSelectedItemId } = useOpenItemsContext();
  const [searchResult, setSearchResult] = useState<SearchResults[]>(allUserItems.map(item => (
    {
      item,
      excerpts: [],
      textHitCount: 1
    }
  )).sort((a, b) => a.item.name.localeCompare(b.item.name)));

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [lastOpenedItemId, setLastOpenedItemId] = useState<string|null>(selectedItemId);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultItemRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    setSelectedItemId(null);
  }, [setSelectedItemId]);

  useEffect(() => {
    if (selectedIndex >= 0) {
      resultItemRefs.current[selectedIndex]?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [selectedIndex, searchResult]);

  const search = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
    setSelectedIndex(-1); // Reset selection when search result changes
    if (e.target.value.trim() === '') {
      setSearchResult(allUserItems.map(item => (
        {
          item,
          excerpts: [],
          textHitCount: 1
        }
      )).sort((a, b) => a.item.name.localeCompare(b.item.name)));
      return;
    }
    const lowercaseSearchString = e.target.value.toLowerCase();
    const newSearchResult: SearchResults[] = [];
    const handleItem = (item: SquiggleItem) => {
      const cleanText = cleanTextForSearch(item.text);
      const textHitPos: number = cleanText.toLowerCase().indexOf(lowercaseSearchString);
      const nameHitPos: number = item.name.toLowerCase().indexOf(lowercaseSearchString);
      const aliasHit: boolean = item.aliases.some(alias => alias.toLowerCase().indexOf(lowercaseSearchString) > -1);
      const excerpts: string[] = [];
      let hitsInText = 0;
      if (textHitPos > -1) {
        let fromIndex = 0;
        while (true) {
          const hitPos = cleanText.toLowerCase().indexOf(lowercaseSearchString, fromIndex);
          if (hitPos === -1) {
            break;
          }
          if (hitsInText >= 5) {
            excerpts.push("(\u2026)");
            break;
          }
          fromIndex = hitPos + lowercaseSearchString.length;
          const excerptStartPos = Math.max(0, hitPos - 25);
          const excerpt = cleanText.substring(excerptStartPos, excerptStartPos + 80);
          const fullExcerpt = (excerptStartPos === 0 ? "" : "\u2026") + excerpt;
          if (fullExcerpt !== excerpts[excerpts.length - 1]) {
            excerpts.push(fullExcerpt);
            hitsInText++;
          }
          if (lowercaseSearchString.length < 3) {
            break;
          }
        }
        newSearchResult.push({ item, excerpts, nameHitPos: nameHitPos > -1 ? nameHitPos : (aliasHit ? 10 : undefined), textHitCount: hitsInText });
      } else if (nameHitPos > -1 || aliasHit) {
        newSearchResult.push({ item, excerpts: [cleanText.substring(0, 80)], nameHitPos: nameHitPos > -1 ? nameHitPos : (aliasHit ? 10 : undefined), textHitCount: 0 });
      }
    };
    for (const item of allUserItems) {
      handleItem(item);
    }

    if (newSearchResult.length === 0) {
      newSearchResult.push({ item: { id: undefined, name: "No results" }, excerpts: [], textHitCount: 0 });
    }
    setSearchResult(newSearchResult.sort((a, b) => {
      if (a.nameHitPos !== undefined || b.nameHitPos !== undefined) {
        if (a.nameHitPos === undefined) {
          return 1;
        }
        if (b.nameHitPos === undefined) {
          return -1;
        }
        if (a.nameHitPos === b.nameHitPos) {
          if (a.item.name.length === b.item.name.length) {
            return a.item.name.localeCompare(b.item.name);
          }
          return a.item.name.length - b.item.name.length;
        }
        return a.nameHitPos - b.nameHitPos;
      }
      if (a.textHitCount !== b.textHitCount) {
        return b.textHitCount - a.textHitCount;
      }
      return a.item.name.localeCompare(b.item.name);
    }));
  }, [setSearchString, allUserItems]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      const targetIndex = selectedIndex >= 0 ? selectedIndex : 0; // Default to the top result if nothing is selected
      if (searchResult[targetIndex]?.item.id) {
        openItem(searchResult[targetIndex].item.id);
        setLastOpenedItemId(searchResult[targetIndex].item.id);
      }
    } else if (e.key === 'ArrowDown') {
      setSelectedIndex((prevIndex) => Math.min(prevIndex + 1, searchResult.length - 1));
    } else if (e.key === 'ArrowUp') {
      setSelectedIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    } else if (e.key === 'Escape') {
      close(lastOpenedItemId);
    }
    e.stopPropagation();
  }, [searchResult, selectedIndex, openItem, setLastOpenedItemId, close, lastOpenedItemId]);

  const clickCallback = useCallback((itemId: string|undefined) => {
    if (itemId) {
      openItem(itemId);
      setLastOpenedItemId(itemId);
    }
    inputRef.current?.focus();
  }, [openItem, setLastOpenedItemId, inputRef]);

  const escapedSearchString = searchString.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const highlightRegex = searchString ? new RegExp(`(${escapedSearchString})`, 'gi') : null;

  return <div className="searchbackdrop" onClick={() => close(lastOpenedItemId) }>
    <div className="search" onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
      <div className="searchbox">
        <input
          type="text"
          value={searchString}
          placeholder="Search..."
          autoFocus
          onChange={search}
          ref={inputRef} />
      </div>
      <div className="searchresults">
        {
          searchResult.map((result, index) =>
            <div
              key={result.item.id}
              ref={(el) => { resultItemRefs.current[index] = el; }}
              className={
                "searchitem " +
                (result.item.type ?? "") +
                (index === selectedIndex ? " selected" : "")
              }
              onClick={() => clickCallback(result.item.id)}
              onMouseEnter={() => setSelectedIndex(index)}>
              <div className="resultname">{result.item.name}</div>
              { result.excerpts.map((excerpt, i) =>
              <div key={i} className="resultexcerpt">{
                (highlightRegex ? excerpt.split(highlightRegex) : [excerpt]).map((part, j) =>
                  part.toLowerCase() === searchString.toLowerCase() ? <span className="highlight" key={j}>{part}</span> : part
                )
              }</div>) }
            </div>
          )
        }
      </div>
    </div>
  </div>;
}

export default Search;